import { useApolloClient, useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  DocumentFilePicker,
  extensionFromName,
  FullScreenDocumentPreview,
  useDocumentFiles,
} from "components/Document";
import { gql } from "generated-graphql";
import { DocumentType } from "generated-graphql/graphql";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import compact from "lodash-es/compact";
import pluralize from "pluralize";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useState } from "react";
import { FileRejection } from "react-dropzone";

import { TENANT_CATALOG_CHEMICALS } from "./schema";

const MAX_FILES = 10;

const QUEUE_SDS_PARSING_JOBS = gql(`
  mutation QueueSdsParsingJobs($input: CreateChemicalsFromSDSInput!) {
    queueSdsParsingJobs(input: $input) {
      id
      status
    }
  }
`);

/**
 * Upload safety data sheets and hand each one to SDS parsing, which builds a
 * catalog chemical from it. Progress moves to the analyzing dialog, which can be
 * minimized to the tray while parsing runs.
 */
export function AddChemicalFromSdsDialog({
  open,
  onClose,
  tenantId,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
}) {
  const alerts = useAlerts();
  const apolloClient = useApolloClient();
  const { createDialog } = useMinimizedDialogs();

  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  const {
    files,
    canAddMore,
    handleDrop,
    removeFile,
    previewFile,
    openPreview,
    closePreview,
    previewUrl,
    resetFiles,
    hasPossibleDuplicates,
  } = useDocumentFiles({ multiple: true, maxFiles: MAX_FILES });

  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments();
  const [queueSdsParsingJobs] = useMutation(QUEUE_SDS_PARSING_JOBS);

  const handleClose = useCallback(() => {
    resetFiles();
    setDropRejectedFiles([]);
    onClose();
  }, [resetFiles, onClose]);

  const launchAnalyzingDialog = useCallback(
    (jobIds: string[]) => {
      const dialogId = `sds-analyzing-${jobIds.join("-")}`;
      createDialog({
        id: dialogId,
        dialogType: DialogType.SdsAnalyzing,
        props: {
          open: true,
          dialogId,
          tenantId,
          jobIds,
          onComplete: () => {
            apolloClient.refetchQueries({ include: [TENANT_CATALOG_CHEMICALS] });
          },
        },
      });
    },
    [createDialog, tenantId, apolloClient]
  );

  const handleUpload = useCallback(async () => {
    setUploadLoading(true);
    try {
      const keys = await uploadFiles(files.map((file) => ({ file, documentData: { tenantId } })));
      const storageLinks = compact(keys);
      if (storageLinks.length !== keys.length) {
        throw new Error("Some files failed to upload. Please try again.");
      }

      const createdDocuments = await upsertDocuments({
        documents: storageLinks.map((storageLink, index) => ({
          tenantId,
          title: files[index].name,
          fileExtension: extensionFromName(files[index].name),
          storageLink,
          documentType: DocumentType.SafetyDataSheet,
          isArchived: false,
        })),
        currentSubmissionDocument: false,
      });

      const documentIds = createdDocuments?.map((document) => document.id) ?? [];
      if (documentIds.length === 0) {
        throw new Error("The uploaded documents could not be saved. Please try again.");
      }

      const queued = await queueSdsParsingJobs({
        variables: { input: { tenantId, documentIds } },
      });
      const jobIds = queued.data?.queueSdsParsingJobs.map((job) => job.id) ?? [];

      if (jobIds.length === 0) {
        throw new Error("The uploaded documents could not be queued for reading.");
      }

      const skippedCount = documentIds.length - jobIds.length;
      if (skippedCount > 0) {
        alerts.warning(
          `${skippedCount} ${pluralize("file", skippedCount)} could not be read and ${skippedCount === 1 ? "was" : "were"} saved to your documents instead.`
        );
      }

      launchAnalyzingDialog(jobIds);
      handleClose();
    } catch (error) {
      alerts.error("Something went wrong while uploading your safety data sheets.", error);
    } finally {
      setUploadLoading(false);
    }
  }, [
    uploadFiles,
    files,
    tenantId,
    upsertDocuments,
    queueSdsParsingJobs,
    alerts,
    launchAnalyzingDialog,
    handleClose,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        title="Add Chemical from SDS"
        primaryActionButtonProps={{
          hideStartIcon: true,
          onClick: handleUpload,
          disabled: files.length === 0,
          saveText: "Upload & Autofill",
          loading: uploadLoading,
          loadingText: "Uploading...",
        }}
      >
        <DocumentFilePicker
          files={files}
          hasPossibleDuplicates={hasPossibleDuplicates}
          onDrop={handleDrop}
          onRemove={removeFile}
          onPreview={openPreview}
          canAddMore={canAddMore}
          multiple
          maxFiles={MAX_FILES}
          accept=".pdf"
          disabled={uploadLoading}
          helperText={`Drag and drop or select up to ${MAX_FILES} safety data sheets (PDF). We'll read each one and create a chemical from it for you to review.`}
          dropRejectedFiles={dropRejectedFiles}
          setDropRejectedFiles={setDropRejectedFiles}
        />
      </Dialog>

      {previewFile && (
        <FullScreenDocumentPreview
          open={Boolean(previewFile)}
          onClose={closePreview}
          fileExtension={extensionFromName(previewFile.name)}
          fileUrl={previewUrl}
          title={previewFile.name}
        />
      )}
    </>
  );
}
